import Translate from "@/components/base/translate/Translate";
import Layout from "../profileLayout";
import styles from "./orderHistoryPage.module.css";
import { useData } from "@/lib/api/api";
import Text from "@/components/base/text";
import Pagination from "@/components/search/pagination/Pagination";

import Link from "@/components/base/link";
import useBreakpoint from "@/components/hooks/useBreakpoint";
import { useModal } from "@/components/_modal";
import { orderHistory } from "@/lib/api/order.fragments";
import { useEffect, useRef, useState } from "react";
import * as userFragments from "@/lib/api/user.fragments";
import Skeleton from "@/components/base/skeleton/Skeleton";
import { getWorkUrlForProfile } from "@/components/profile/utils";
import { parseDate, getCreatorDisplay } from "@/lib/utils";
import useAuthentication from "@/components/hooks/user/useAuthentication";

const ITEMS_PER_PAGE = 20;

/**
 * Shows the previous orders made by the user from bibliotekdk.
 *
 * @returns {React.JSX.Element}
 *
 */

export default function OrderHistoryPage() {
  const { hasCulrUniqueId } = useAuthentication();
  const breakpoint = useBreakpoint();
  const modal = useModal();
  const isMobile = ["xs", "sm", "md"].includes(breakpoint);

  const tableTopRef = useRef(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [orderHistoryData, setOrderHistoryData] = useState([]);
  //fetch paginated orderhistorydata
  const { data, isLoading } = useData(
    hasCulrUniqueId &&
      orderHistory({
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
      })
  );
  const { data: userData, mutate } = useData(
    hasCulrUniqueId && userFragments.extendedData()
  );
  const persistUserData = !!userData?.user?.persistUserData;

  const onPageChange = async (newPage, scrollToTop = true) => {
    if (newPage > totalPages) {
      newPage = totalPages;
    }
    setCurrentPage(newPage);

    if (scrollToTop && !isMobile) {
      tableTopRef?.current?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (data) {
      const fetchedData = data?.user?.bibliotekDkOrders?.result;
      const pages = Math.ceil(
        data?.user?.bibliotekDkOrders?.hitcount / ITEMS_PER_PAGE
      );
      setTotalPages(pages);
      if (fetchedData) {
        // on mobile, merge the previous data with the new fetched data. On desktop show only one page at a time
        setOrderHistoryData((prevData) =>
          isMobile ? [...prevData, ...fetchedData] : fetchedData
        );
      }
    }
  }, [data, isLoading, isMobile]);

  useEffect(() => {
    if (!modal.isVisible) {
      //hacked solution. Calling mutate() directley does not refetch user consent data.
      setTimeout(mutate, 200);
    }
  }, [modal.isVisible]);

  if (isLoading) {
    return (
      <Layout title={Translate({ context: "profile", label: "orderHistory" })}>
        <Skeleton lines={2} className={styles.skeletonText} />
        <div className={styles.skeletonContainer}>
          <Skeleton className={styles.skeleton} />
          <Skeleton className={styles.skeleton} />
          <Skeleton className={styles.skeleton} />
          <Skeleton className={styles.skeleton} />
        </div>
      </Layout>
    );
  }
  return (
    <Layout title={Translate({ context: "profile", label: "orderHistory" })}>
      {persistUserData ? (
        <Text type="text3">
          {Translate({ context: "profile", label: "consentInfoTextpart1" })}
          <Link
            className={styles.consentLink}
            onClick={() => {
              modal.push("orderHistoryDataConsent");
            }}
            border={{
              top: false,
              bottom: {
                keepVisible: true,
              },
            }}
          >
            {Translate({ context: "profile", label: "consent" })}
          </Link>
          {Translate({ context: "profile", label: "consentInfoTextpart2" })}
        </Text>
      ) : (
        <>
          <Text className={styles.orderHistoryInfo}>
            {Translate({ context: "profile", label: "orderHistoryInfo" })}
          </Text>

          <Link
            onClick={() => {
              modal.push("orderHistoryDataConsent");
            }}
            border={{
              top: false,
              bottom: {
                keepVisible: true,
              },
            }}
          >
            <Text type="text3" tag="span">
              {Translate({ context: "profile", label: "canWeSaveYourOrders" })}
            </Text>
          </Link>
        </>
      )}

      <div ref={tableTopRef} />
      {isMobile ? (
        <>
          <div className={styles.headerRow}>
            <Text className={styles.headerItem}>
              {Translate({ context: "profile", label: "date" })}
            </Text>
            <Text className={styles.headerItem}>
              {Translate({ context: "profile", label: "activity" })}
            </Text>
            <Text className={styles.headerItem}>
              {Translate({ context: "profile", label: "orderNumber" })}
            </Text>
          </div>

          {totalPages == 0 ? (
            <Text className={styles.emptyListText}>
              {Translate({ context: "profile", label: "emptyOrderList" })}
            </Text>
          ) : (
            orderHistoryData?.map((order, index) => {
              const key =
                order?.orderId ||
                `${order?.creationDate}-${order?.work?.workId || index}`;
              return <TableItem order={order} key={key} />;
            })
          )}
        </>
      ) : (
        <table className={styles.orderHistoryTable}>
          <thead>
            <tr className={styles.headerRow}>
              <th className={styles.headerItem}>
                {Translate({ context: "profile", label: "date" })}
              </th>
              <th className={styles.headerItem}>
                {Translate({ context: "profile", label: "activity" })}
              </th>
              <th className={styles.headerItem}>
                {Translate({ context: "profile", label: "orderNumber" })}
              </th>
            </tr>
          </thead>
          {totalPages == 0 ? (
            <Text className={styles.emptyListText}>
              {Translate({ context: "profile", label: "emptyOrderList" })}
            </Text>
          ) : (
            <tbody>
              {orderHistoryData?.map((order, index) => {
                const key =
                  order?.orderId ||
                  `${order?.creationDate}-${order?.work?.workId || index}`;
                return <TableItem order={order} key={key} />;
              })}
            </tbody>
          )}
        </table>
      )}
      {totalPages > 1 && (
        <Pagination
          className={styles.pagination}
          numPages={totalPages}
          currentPage={parseInt(currentPage, 10)}
          onChange={onPageChange}
        />
      )}
    </Layout>
  );
}

/**
 * TableItem shows info for a single order.
 * @param {Object} props
 * @returns {React.JSX.Element}
 */
function TableItem({ order }) {
  const breakpoint = useBreakpoint();

  if (!order) {
    return null;
  }
  const isMobile = breakpoint === "xs";
  const { orderId, creationDate, work } = order;
  const isDigitalOrder = !orderId; //digital acrticle service orders do not have orderId

  const { day, monthName, isToday, hours, minutes } = parseDate(creationDate);
  const time = `Kl. ${hours}.${minutes}`;
  const dateString = isToday
    ? Translate({ context: "profile", label: "last-day" })
    : `D. ${day}. ${monthName}`;

  if (isMobile) {
    return (
      <div className={styles.tableItem}>
        <div>
          <Text type="text1">
            {Translate({
              context: "profile",
              label: isDigitalOrder
                ? "digitalOrderRegistered"
                : "orderRegistered",
            })}
          </Text>
          <Text className={styles.mobileDate} type="text3">
            {dateString}
          </Text>
          <WorkInfo
            workId={work?.workId}
            titles={work?.titles}
            creators={work?.creators}
            isDigitalOrder={isDigitalOrder}
          />
        </div>
        <div className={styles.orderNumber}>
          {breakpoint === "xs" && (
            <Text className={styles.orderNumberText} type="text4">
              {`${Translate({ context: "profile", label: "orderNumber" })}:`}
            </Text>
          )}
          {orderId && <Text type="text3">{orderId} </Text>}
        </div>
      </div>
    );
  }
  return (
    <tr className={styles.tableItem}>
      <td className={styles.date}>
        {!isMobile && (
          <>
            <Text type="text3">{dateString}</Text>
            <Text type="text3">{time}</Text>
          </>
        )}
      </td>
      <td className={styles.activity}>
        <WorkInfo
          workId={work?.workId}
          titles={work?.titles}
          creators={work?.creators}
          isDigitalOrder={isDigitalOrder}
        />
      </td>
      <td className={styles.orderNumber}>
        {breakpoint === "xs" && orderId && (
          <Text className={styles.orderNumberText} type="text4">
            {`${Translate({ context: "profile", label: "orderNumber" })}:`}
          </Text>
        )}
        {orderId && <Text type="text3">{orderId}</Text>}
      </td>
    </tr>
  );
}

/**
 * Used in TableItem. Shows info (like title, author, link to work) for a given order
 * @returns
 */
function WorkInfo({ workId, titles, creators, isDigitalOrder }) {
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "xs";
  const title = titles?.main?.[0];
  const creator = getCreatorDisplay(creators?.[0]);
  const workTitle =
    title || Translate({ context: "profile", label: "titleNotFound" });

  return (
    <>
      {!isMobile && (
        <Text type="text3">
          {Translate({
            context: "profile",
            label: isDigitalOrder
              ? "digitalOrderRegistered"
              : "orderRegistered",
          })}
        </Text>
      )}
      <Text type="text2" className={styles.orderWorkInfo}>
        {Translate({
          context: "profile",
          label: isDigitalOrder
            ? "youHaveOrderedDigitalOrder"
            : "youHaveOrdered",
        }) + " "}
        {workId ? (
          <Link
            href={getWorkUrlForProfile({ workId, titles, creators })}
            border={{
              top: false,
              bottom: {
                keepVisible: true,
              },
            }}
          >
            <Text type="text1" tag="span">
              {workTitle}
            </Text>
          </Link>
        ) : (
          <Text type="text1" tag="span">
            {workTitle}
          </Text>
        )}
        {creator &&
          !isDigitalOrder &&
          ` ${Translate({ context: "general", label: "by" })} ${creator}`}
      </Text>
    </>
  );
}
